import type { PrototypeContentApiClient } from './apiContentClient';
import type { PrototypeApiClient } from './apiClient';
import type { ChapterPageModel, TopicPageModel } from '../../shared/pages/pageModels';
import { renderChapterStructuredData, renderTopicStructuredData } from '../../shared/pages/renderStructuredData';
import { resolveSeoPrimaryImageFact } from '../../shared/pages/seoImage';
import { brandConfig } from '../../config/brandConfig';

type CoursePageModel = TopicPageModel | ChapterPageModel;

const BRAND_NAME = brandConfig.name;

let navigationGeneration = 0;

export function applyCourseDocumentSeo(doc: Document, model: CoursePageModel): void {
	const { seo } = model;
	const image = resolveSeoPrimaryImageFact(seo);
	doc.title = seo.title;
	upsertMeta(doc, 'name', 'description', seo.metaDescription);
	upsertLink(doc, 'canonical', seo.canonical);
	upsertMeta(doc, 'name', 'robots', seo.robots);
	upsertMeta(doc, 'property', 'og:type', seo.ogType);
	upsertMeta(doc, 'property', 'og:site_name', BRAND_NAME);
	upsertMeta(doc, 'property', 'og:title', seo.title);
	upsertMeta(doc, 'property', 'og:description', seo.metaDescription);
	upsertMeta(doc, 'property', 'og:url', seo.canonical);
	upsertMeta(doc, 'property', 'og:image', image.url);
	upsertMeta(doc, 'property', 'og:image:width', '1200');
	upsertMeta(doc, 'property', 'og:image:height', String(image.height));
	upsertMeta(doc, 'property', 'og:image:alt', seo.title);
	upsertMeta(doc, 'property', 'og:locale', 'en_US');
	upsertMeta(doc, 'name', 'twitter:card', 'summary_large_image');
	upsertMeta(doc, 'name', 'twitter:title', seo.title);
	upsertMeta(doc, 'name', 'twitter:description', seo.metaDescription);
	upsertMeta(doc, 'name', 'twitter:image', image.url);
	upsertMeta(doc, 'name', 'twitter:image:alt', seo.title);

	for (const property of ['article:published_time', 'article:modified_time', 'article:section']) removeMeta(doc, 'property', property);
	if (model.pageType === 'chapter') {
		upsertMeta(doc, 'property', 'article:published_time', model.chapter.createdAt ?? '');
		upsertMeta(doc, 'property', 'article:modified_time', model.chapter.updatedAt ?? '');
		upsertMeta(doc, 'property', 'article:section', model.topic.category);
	}

	doc.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
	const structuredData = doc.createElement('script');
	structuredData.id = 'course-structured-data';
	structuredData.type = 'application/ld+json';
	structuredData.textContent = model.pageType === 'topic' ? renderTopicStructuredData(model) : renderChapterStructuredData(model);
	doc.head.appendChild(structuredData);
}

export function syncTopicDocumentSeo(api: PrototypeContentApiClient, slug: string): void {
	void syncDocumentSeo(api, `/api/pages/topic/${encodeURIComponent(slug)}`, `/topics/${encodeURIComponent(slug)}`);
}

export function syncChapterDocumentSeo(api: PrototypeContentApiClient, slug: string, chapterIndex: number): void {
	void syncDocumentSeo(api, `/api/pages/chapter/${encodeURIComponent(slug)}/${chapterIndex}`, `/topics/${encodeURIComponent(slug)}/chapters/${chapterIndex}`);
}

/**
 * Async SEO sync for community idea detail pages. Fetches the idea via
 * the API and applies title, meta, canonical, and public WebPage JSON-LD
 * to the document head.
 *
 * Mirrors the server-side renderPublicIdeaHtml (ideaSeoHtml.ts) so that
 * Googlebot Wave 2 (rendered DOM) matches Wave 1 (SSR HTML).
 *
 * Uses the same navigationGeneration counter as syncDocumentSeo so
 * resetSpaDocumentSeo can invalidate in-flight idea fetches.
 */
export function syncIdeaDocumentSeo(api: PrototypeApiClient, slug: string): void {
	void (async () => {
		const generation = ++navigationGeneration;
		const expectedPath = `/community/ideas/${encodeURIComponent(slug)}`;
		try {
			const resp = await api.getCommunityIdea(slug);
			// Stale check: a newer navigation happened, or user navigated away.
			if (generation !== navigationGeneration || window.location.pathname !== expectedPath || !resp.idea) return;
			applyIdeaDocumentSeo(document, resp.idea, slug);
		} catch {
			// Head synchronization is enhancement-only.
		}
	})();
}

/** Idea data needed for SEO - mirrors the safe CommunityIdeaDetailResponse.idea projection. */
export interface IdeaSeoInput {
	id: string;
	title: string;
	subtitle: string | null;
	isAdopted: boolean;
	displayStatus: 'Voting' | 'Selected';
	publicIndexable: boolean;
}

/**
 * Apply direct-reader metadata. Proposal pages remain user-navigable but are
 * intentionally noindex and omit structured data / sitemap discovery.
 */
export function applyIdeaDocumentSeo(doc: Document, idea: IdeaSeoInput, slug: string): void {
	const origin = window.location.origin;
	const canonical = `${origin}/community/ideas/${encodeURIComponent(slug)}`;
	const isPublic = idea.publicIndexable;
	const title = isPublic ? `${idea.title} — ${BRAND_NAME} Community Picks` : `Community pick status — ${BRAND_NAME}`;
	const description = isPublic ? idea.subtitle ?? `A community pick on ${BRAND_NAME}.` : `This ${BRAND_NAME} Community pick is no longer publicly available.`;
	const robots = 'noindex,nofollow';
	const image = new URL('/assets/brand/deepmox-og-1200x630-v2.png', origin).href;

	doc.title = title;
	upsertMeta(doc, 'name', 'description', description);
	upsertLink(doc, 'canonical', canonical);
	upsertMeta(doc, 'name', 'robots', robots);
	upsertMeta(doc, 'property', 'og:type', 'article');
	upsertMeta(doc, 'property', 'og:site_name', BRAND_NAME);
	upsertMeta(doc, 'property', 'og:title', title);
	upsertMeta(doc, 'property', 'og:description', description);
	upsertMeta(doc, 'property', 'og:url', canonical);
	upsertMeta(doc, 'property', 'og:image', image);
	upsertMeta(doc, 'property', 'og:image:width', '1200');
	upsertMeta(doc, 'property', 'og:image:height', '630');
	upsertMeta(doc, 'property', 'og:image:alt', title);
	upsertMeta(doc, 'property', 'og:locale', 'en_US');
	upsertMeta(doc, 'name', 'twitter:card', 'summary_large_image');
	upsertMeta(doc, 'name', 'twitter:title', title);
	upsertMeta(doc, 'name', 'twitter:description', description);
	upsertMeta(doc, 'name', 'twitter:image', image);
	upsertMeta(doc, 'name', 'twitter:image:alt', title);

	// Proposal direct pages are not SEO documents. Remove any previous route's
	// structured data instead of advertising this user-navigable reader.
	doc.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
}

async function syncDocumentSeo(api: PrototypeContentApiClient, endpoint: string, expectedPath: string): Promise<void> {
	const generation = ++navigationGeneration;
	try {
		const model = await api.getCoursePageModel(endpoint);
		if (generation !== navigationGeneration || window.location.pathname !== expectedPath || !isCoursePageModel(model)) return;
		applyCourseDocumentSeo(document, model);
	} catch {
		// Head synchronization is enhancement-only; content navigation remains usable.
	}
}

export function upsertMeta(doc: Document, attribute: 'name' | 'property', key: string, content: string): void {
	const selector = `meta[${attribute}="${key}"]`;
	const existing = [...doc.querySelectorAll<HTMLMetaElement>(selector)];
	const meta = existing.shift() ?? doc.createElement('meta');
	meta.setAttribute(attribute, key);
	meta.content = content;
	existing.forEach((node) => node.remove());
	if (!meta.isConnected) doc.head.appendChild(meta);
}

export function removeMeta(doc: Document, attribute: 'name' | 'property', key: string): void {
	doc.querySelectorAll(`meta[${attribute}="${key}"]`).forEach((node) => node.remove());
}

export function upsertLink(doc: Document, rel: string, href: string): void {
	const existing = [...doc.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`)];
	const link = existing.shift() ?? doc.createElement('link');
	link.rel = rel;
	link.href = href;
	existing.forEach((node) => node.remove());
	if (!link.isConnected) doc.head.appendChild(link);
}

/**
 * Increment the navigation generation counter so any in-flight
 * syncDocumentSeo / syncIdeaDocumentSeo promises know they are stale
 * and skip applying their results. Used by resetSpaDocumentSeo before
 * it mutates the head, to prevent a late-arriving course/idea fetch
 * from overwriting the SPA route's head.
 */
export function bumpNavigationGeneration(): void {
	navigationGeneration++;
}

export function getNavigationGeneration(): number {
	return navigationGeneration;
}

function isCoursePageModel(value: unknown): value is CoursePageModel {
	if (!value || typeof value !== 'object') return false;
	const pageType = (value as { pageType?: unknown }).pageType;
	return pageType === 'topic' || pageType === 'chapter';
}
